import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Calendar, Users, FileText, Upload, X, CalendarCheck, Link2, Unlink, Palmtree, Trash2, RefreshCw, Globe, Bot, Send, AlertTriangle, CheckCircle, AlertCircle, Activity, BookOpen, Download, XCircle } from 'lucide-react';
import { TIMEZONE_OPTIONS, convertFromIST, formatTimeDisplay, getTimezoneAbbreviation } from '../utils/timezone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor: auto-logout on 401 (expired JWT)
const authAxios = axios.create();
authAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('admin_token');
      window.location.href = '/admin/login?expired=true';
    }
    return Promise.reject(error);
  }
);

const AdminDashboard = () => {
  const [searchParams] = useSearchParams();
  const [sessions, setSessions] = useState([]);
  const [resources, setResources] = useState([]);
  const [slots, setSlots] = useState([]);
  const [prices, setPrices] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [calendarStatus, setCalendarStatus] = useState({ connected: false, email: null });
  const [connectingCalendar, setConnectingCalendar] = useState(false);
  const [lastCalendarSync, setLastCalendarSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const syncIntervalRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('sessions');
  const [workshopData, setWorkshopData] = useState(null);
  const [workshopRegistrations, setWorkshopRegistrations] = useState([]);
  const [workshopLoading, setWorkshopLoading] = useState(false);
  const [showResourceForm, setShowResourceForm] = useState(false);
  const [showSlotForm, setShowSlotForm] = useState(false);
  const [showVacationForm, setShowVacationForm] = useState(false);
  const [editingPrice, setEditingPrice] = useState(null);
  const [resourceForm, setResourceForm] = useState({
    title: '',
    description: '',
    file_url: '',
    category: ''
  });
  const [slotForm, setSlotForm] = useState({
    start_date: '',
    end_date: '',
    weekdays: [],
    times: '',
    slot_type: 'individual'
  });
  const [vacationForm, setVacationForm] = useState({
    start_date: '',
    end_date: '',
    reason: ''
  });
  const [adminTimezone, setAdminTimezone] = useState('Asia/Kolkata');
  
  // Website Manager Agent State
  const [agentMessages, setAgentMessages] = useState([]);
  const [agentInput, setAgentInput] = useState('');
  const [agentLoading, setAgentLoading] = useState(false);
  const [systemHealth, setSystemHealth] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const agentChatRef = useRef(null);

  const weekdayOptions = [
    { value: 0, label: 'Monday' },
    { value: 1, label: 'Tuesday' },
    { value: 2, label: 'Wednesday' },
    { value: 3, label: 'Thursday' },
    { value: 4, label: 'Friday' },
    { value: 5, label: 'Saturday' },
    { value: 6, label: 'Sunday' }
  ];

  useEffect(() => {
    // Validate token on load
    const token = localStorage.getItem('admin_token');
    if (!token) {
      window.location.href = '/admin/login';
      return;
    }
    
    fetchData();
    
    // Check for calendar connection callback
    if (searchParams.get('calendar_connected') === 'true') {
      toast.success('Google Calendar connected successfully!');
      window.history.replaceState({}, '', '/admin/dashboard');
    } else if (searchParams.get('calendar_error')) {
      toast.error('Failed to connect Google Calendar. Please try again.');
      window.history.replaceState({}, '', '/admin/dashboard');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Auto-sync calendar every 15 minutes
  useEffect(() => {
    if (calendarStatus.connected) {
      // Initial sync
      syncCalendar(true);
      
      // Set up 15-minute interval
      syncIntervalRef.current = setInterval(() => {
        syncCalendar(true);
      }, 15 * 60 * 1000); // 15 minutes
      
      return () => {
        if (syncIntervalRef.current) {
          clearInterval(syncIntervalRef.current);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarStatus.connected]);

  // Auto-refresh Website Manager health every 60 seconds when the agent tab is active
  useEffect(() => {
    if (activeTab === 'agent') {
      fetchSystemHealth();
      const healthInterval = setInterval(() => {
        fetchSystemHealth();
      }, 60 * 1000);
      return () => clearInterval(healthInterval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Fetch workshop data when workshop tab is active
  useEffect(() => {
    if (activeTab === 'workshop') {
      fetchWorkshopData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchWorkshopData = async () => {
    setWorkshopLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const [workshopsRes, regsRes] = await Promise.all([
        axios.get(`${API}/admin/workshops`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/workshop/book-journaling-midnight-library/registrations`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const ws = workshopsRes.data.find(w => w.id === 'book-journaling-midnight-library');
      setWorkshopData(ws || null);
      setWorkshopRegistrations(regsRes.data || []);
    } catch (err) {
      console.error('Failed to fetch workshop data:', err);
    } finally {
      setWorkshopLoading(false);
    }
  };

  const cancelWorkshopRegistration = async (registrationId, participantName) => {
    if (!window.confirm(`Cancel registration for ${participantName}?\n\nThis will:\n• Free up their seat(s)\n• Initiate a full refund\n• Send a cancellation email`)) return;
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.post(
        `${API}/admin/workshop/book-journaling-midnight-library/registrations/${registrationId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      fetchWorkshopData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel registration');
    }
  };

  const downloadWorkshopCSV = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(
        `${API}/admin/workshop/book-journaling-midnight-library/registrations/download`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'workshop_registrations.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download CSV');
    }
  };

  // Sync calendar data (refreshes slots based on calendar busy times)
  const syncCalendar = async (silent = false) => {
    if (syncing) return;
    
    setSyncing(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await authAxios.post(`${API}/calendar/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLastCalendarSync(new Date());
      
      if (!silent) {
        toast.success('Calendar synced successfully!');
      }
      
      // Refresh slots data
      const slotsRes = await authAxios.get(`${API}/admin/slots`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setSlots(slotsRes.data);
      
    } catch (error) {
      if (!silent) {
        toast.error('Failed to sync calendar');
      }
      console.error('Calendar sync error:', error);
    } finally {
      setSyncing(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const [sessionsRes, resourcesRes, slotsRes, pricesRes, calendarRes, vacationsRes] = await Promise.all([
        authAxios.get(`${API}/sessions`, { headers: { Authorization: `Bearer ${token}` } }),
        authAxios.get(`${API}/resources`, { headers: { Authorization: `Bearer ${token}` } }),
        authAxios.get(`${API}/admin/slots`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/prices`),
        authAxios.get(`${API}/calendar/status`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { connected: false } })),
        authAxios.get(`${API}/admin/vacations`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setSessions(sessionsRes.data);
      setResources(resourcesRes.data);
      setSlots(slotsRes.data);
      setPrices(pricesRes.data);
      setCalendarStatus(calendarRes.data);
      setVacations(vacationsRes.data);
      
      // Also fetch system health for the agent
      fetchSystemHealth();
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Website Manager Agent Functions
  const fetchSystemHealth = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await authAxios.get(`${API}/agent/health`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSystemHealth(response.data);
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Failed to fetch system health:', error);
    }
  };

  const sendAgentMessage = async () => {
    if (!agentInput.trim()) return;
    
    const userMessage = agentInput.trim();
    setAgentInput('');
    
    // Add user message to chat
    setAgentMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);
    
    setAgentLoading(true);
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await authAxios.post(`${API}/agent/chat`, 
        { content: userMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Add agent response to chat
      setAgentMessages(prev => [...prev, {
        role: 'agent',
        content: response.data.response,
        timestamp: response.data.timestamp
      }]);
      
      // Update system health and alerts
      if (response.data.services_status) {
        setSystemHealth(prev => ({
          ...prev,
          services: response.data.services_status
        }));
      }
      if (response.data.alerts) {
        setAlerts(response.data.alerts);
      }
      
      // Scroll to bottom of chat
      setTimeout(() => {
        if (agentChatRef.current) {
          agentChatRef.current.scrollTop = agentChatRef.current.scrollHeight;
        }
      }, 100);
      
    } catch (error) {
      toast.error('Failed to get agent response');
      setAgentMessages(prev => [...prev, {
        role: 'agent',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setAgentLoading(false);
    }
  };

  const handleAgentKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendAgentMessage();
    }
  };

  const updateSessionStatus = async (sessionId, status) => {
    try {
      const token = localStorage.getItem('admin_token');
      await authAxios.patch(
        `${API}/sessions/${sessionId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Session status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update session');
    }
  };

  const createResource = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      await authAxios.post(`${API}/resources`, resourceForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Resource created successfully');
      setShowResourceForm(false);
      setResourceForm({ title: '', description: '', file_url: '', category: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create resource');
    }
  };

  const deleteResource = async (resourceId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await authAxios.delete(`${API}/resources/${resourceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Resource deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete resource');
    }
  };

  const createSlots = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      
      if (!slotForm.start_date || !slotForm.end_date) {
        toast.error('Please select start and end dates');
        return;
      }
      
      if (slotForm.weekdays.length === 0) {
        toast.error('Please select at least one day of the week');
        return;
      }
      
      if (!slotForm.times.trim()) {
        toast.error('Please enter at least one time slot');
        return;
      }
      
      // Parse times (comma-separated)
      const timesList = slotForm.times.split(',').map(t => t.trim()).filter(t => t);
      
      // Show loading toast for large date ranges
      const startDate = new Date(slotForm.start_date);
      const endDate = new Date(slotForm.end_date);
      const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
      
      let loadingToast;
      if (daysDiff > 30) {
        loadingToast = toast.loading(`Creating slots for ${daysDiff} days... This may take a moment.`);
      }
      
      const response = await authAxios.post(`${API}/admin/slots/bulk`, {
        start_date: slotForm.start_date,
        end_date: slotForm.end_date,
        weekdays: slotForm.weekdays,
        times: timesList,
        slot_type: slotForm.slot_type
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000 // 2 minute timeout for large requests
      });
      
      if (loadingToast) {
        toast.dismiss(loadingToast);
      }
      
      toast.success(`${response.data.message}${response.data.skipped_existing ? ` (${response.data.skipped_existing} existing slots skipped)` : ''}`);
      setShowSlotForm(false);
      setSlotForm({ start_date: '', end_date: '', weekdays: [], times: '', slot_type: 'individual' });
      fetchData();
    } catch (error) {
      toast.dismiss(); // Dismiss any loading toasts
      if (error.code === 'ECONNABORTED') {
        toast.error('Request timed out. Please try creating slots for a shorter date range.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to create slots');
      }
    }
  };

  const toggleWeekday = (day) => {
    setSlotForm(prev => ({
      ...prev,
      weekdays: prev.weekdays.includes(day)
        ? prev.weekdays.filter(d => d !== day)
        : [...prev.weekdays, day]
    }));
  };

  const setQuickSchedule = (type) => {
    if (type === 'weekdays') {
      setSlotForm(prev => ({ ...prev, weekdays: [1, 2, 3, 4], times: '11:00, 12:30, 16:00, 17:30' }));
    } else if (type === 'saturday') {
      setSlotForm(prev => ({ ...prev, weekdays: [5], times: '10:30, 12:00, 13:30' }));
    } else if (type === 'all') {
      setSlotForm(prev => ({ ...prev, weekdays: [1, 2, 3, 4, 5], times: '11:00, 12:30, 16:00, 17:30' }));
    }
  };

  const deleteSlot = async (slotId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await authAxios.delete(`${API}/admin/slots/${slotId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Slot deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete slot');
    }
  };

  const connectGoogleCalendar = async () => {
    setConnectingCalendar(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await authAxios.get(`${API}/calendar/auth`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Google OAuth
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error('Failed to initiate Google Calendar connection');
      setConnectingCalendar(false);
    }
  };

  const disconnectGoogleCalendar = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await authAxios.delete(`${API}/calendar/disconnect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Google Calendar disconnected');
      setCalendarStatus({ connected: false, email: null });
    } catch (error) {
      toast.error('Failed to disconnect Google Calendar');
    }
  };

  const updatePrice = async (serviceName, priceData) => {
    try {
      const token = localStorage.getItem('admin_token');
      await authAxios.put(`${API}/admin/prices/${serviceName}`, priceData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Price updated successfully');
      setEditingPrice(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update price');
    }
  };

  const clearAllSlots = async () => {
    if (!window.confirm('Are you sure you want to clear all unbooked slots? This action cannot be undone.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await authAxios.delete(`${API}/admin/slots`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to clear slots');
    }
  };

  const stats = {
    total: sessions.length,
    upcoming: sessions.filter(s => s.status === 'scheduled' || s.status === 'confirmed').length,
    completed: sessions.filter(s => s.status === 'completed').length,
  };

  return (
    <div className="min-h-screen pt-20 px-6 py-16" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-heading font-light text-foreground mb-2" data-testid="admin-heading">
            Admin <span className="italic text-primary">Dashboard</span>
          </h1>
          <p className="text-lg text-muted-foreground">Manage appointments and resources</p>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Card className="bg-white/80 backdrop-blur-sm rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm mb-1">Total Sessions</p>
                <p className="text-3xl font-heading font-bold text-foreground">{stats.total}</p>
              </div>
              <Calendar className="text-foreground" size={32} />
            </div>
          </Card>
          <Card className="bg-white/80 backdrop-blur-sm rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm mb-1">Upcoming</p>
                <p className="text-3xl font-heading font-bold text-primary">{stats.upcoming}</p>
              </div>
              <Users className="text-primary" size={32} />
            </div>
          </Card>
          <Card className="bg-white/80 backdrop-blur-sm rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm mb-1">Completed</p>
                <p className="text-3xl font-heading font-bold text-accent">{stats.completed}</p>
              </div>
              <FileText className="text-accent" size={32} />
            </div>
          </Card>
        </div>

        {/* Tabs Navigation */}
        <div className="mb-8">
          <div className="border-b border-muted">
            <div className="flex gap-8">
              <button
                onClick={() => setActiveTab('sessions')}
                className={`pb-4 px-2 font-medium transition-colors relative ${
                  activeTab === 'sessions' 
                    ? 'text-primary' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="tab-sessions"
              >
                Appointments
                {activeTab === 'sessions' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"></div>
                )}
              </button>
              <button
                onClick={() => setActiveTab('slots')}
                className={`pb-4 px-2 font-medium transition-colors relative ${
                  activeTab === 'slots' 
                    ? 'text-primary' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="tab-slots"
              >
                Availability Slots
                {activeTab === 'slots' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"></div>
                )}
              </button>
              <button
                onClick={() => setActiveTab('pricing')}
                className={`pb-4 px-2 font-medium transition-colors relative ${
                  activeTab === 'pricing' 
                    ? 'text-primary' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="tab-pricing"
              >
                Pricing
                {activeTab === 'pricing' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"></div>
                )}
              </button>
              <button
                onClick={() => setActiveTab('resources')}
                className={`pb-4 px-2 font-medium transition-colors relative ${
                  activeTab === 'resources' 
                    ? 'text-primary' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="tab-resources"
              >
                Resources
                {activeTab === 'resources' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"></div>
                )}
              </button>
              <button
                onClick={() => setActiveTab('calendar')}
                className={`pb-4 px-2 font-medium transition-colors relative ${
                  activeTab === 'calendar' 
                    ? 'text-primary' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="tab-calendar"
              >
                Google Calendar
                {calendarStatus.connected && (
                  <span className="ml-2 w-2 h-2 bg-green-500 rounded-full inline-block"></span>
                )}
                {activeTab === 'calendar' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"></div>
                )}
              </button>
              <button
                onClick={() => setActiveTab('vacations')}
                className={`pb-4 px-2 font-medium transition-colors relative ${
                  activeTab === 'vacations' 
                    ? 'text-primary' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="tab-vacations"
              >
                <Palmtree className="inline-block mr-1 mb-0.5" size={16} />
                Time Off
                {vacations.length > 0 && (
                  <span className="ml-2 px-1.5 py-0.5 bg-primary/10 text-primary text-xs rounded-full">{vacations.length}</span>
                )}
                {activeTab === 'vacations' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"></div>
                )}
              </button>
              <button
                onClick={() => setActiveTab('workshop')}
                className={`pb-4 px-2 font-medium transition-colors relative ${
                  activeTab === 'workshop' 
                    ? 'text-primary' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="tab-workshop"
              >
                <BookOpen className="inline-block mr-1 mb-0.5" size={16} />
                Workshop
                {workshopData && (
                  <span className="ml-2 px-1.5 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                    {workshopData.seats_taken || 0}/{workshopData.max_seats || 8}
                  </span>
                )}
                {activeTab === 'workshop' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"></div>
                )}
              </button>
              <button
                onClick={() => setActiveTab('agent')}
                className={`pb-4 px-2 font-medium transition-colors relative ${
                  activeTab === 'agent' 
                    ? 'text-primary' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="tab-agent"
              >
                <Bot className="inline-block mr-1 mb-0.5" size={16} />
                Website Manager
                {alerts.length > 0 && (
                  <span className="ml-2 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">{alerts.length}</span>
                )}
                {activeTab === 'agent' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"></div>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Sessions Management */}
        {activeTab === 'sessions' && (
        <section className="mb-12" data-testid="sessions-management">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
            <h2 className="text-3xl font-heading font-medium text-foreground">Appointments</h2>
            {/* Timezone Selector for Sessions */}
            <div className="flex items-center gap-2">
              <Globe size={16} className="text-muted-foreground" />
              <Select value={adminTimezone} onValueChange={setAdminTimezone}>
                <SelectTrigger className="w-48 bg-white/50" data-testid="sessions-timezone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {loading ? (
            <p className="text-center text-muted-foreground py-12">Loading sessions...</p>
          ) : sessions.length === 0 ? (
            <Card className="bg-white/80 backdrop-blur-sm rounded-2xl p-12 text-center">
              <p className="text-muted-foreground">No sessions scheduled</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {sessions.map((session) => {
                const converted = convertFromIST(session.session_date, session.session_time, adminTimezone);
                return (
                  <Card key={session.id} className="bg-white/80 backdrop-blur-sm rounded-2xl p-6" data-testid={`admin-session-${session.id}`}>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="text-xl font-heading font-medium text-foreground">{session.client_name}</h3>
                            <p className="text-sm text-muted-foreground">{session.client_email}</p>
                            {session.client_phone && (
                              <p className="text-sm text-muted-foreground">{session.client_phone}</p>
                            )}
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            session.status === 'confirmed' ? 'bg-primary/10 text-primary' :
                            session.status === 'completed' ? 'bg-accent/10 text-accent' :
                            'bg-muted text-muted-foreground'
                          }`}>
                            {session.status}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                          <span>{session.service_type}</span>
                          <span>•</span>
                          <span>
                            {converted.dateChanged ? converted.date : session.session_date} at {formatTimeDisplay(converted.time)} {getTimezoneAbbreviation(adminTimezone)}
                          </span>
                          <span>•</span>
                          <span>₹{session.payment_amount}</span>
                        </div>
                        {session.notes && (
                          <p className="text-sm text-muted-foreground mt-2 italic">Notes: {session.notes}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Select value={session.status} onValueChange={(value) => updateSessionStatus(session.id, value)}>
                          <SelectTrigger className="w-40" data-testid={`status-select-${session.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="scheduled">Scheduled</SelectItem>
                            <SelectItem value="confirmed">Confirmed</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </section>
        )}

        {/* Resources Management */}
        {activeTab === 'resources' && (
        <section data-testid="resources-management">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-heading font-medium text-foreground">Resources</h2>
            <Button
              onClick={() => setShowResourceForm(!showResourceForm)}
              className="bg-primary text-white hover:bg-primary/90 rounded-full"
              data-testid="add-resource-button"
            >
              <Upload size={16} className="mr-2" /> Add Resource
            </Button>
          </div>

          {showResourceForm && (
            <Card className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 mb-6" data-testid="resource-form">
              <form onSubmit={createResource} className="space-y-4">
                <div>
                  <Label>Title</Label>
                  <Input
                    value={resourceForm.title}
                    onChange={(e) => setResourceForm({ ...resourceForm, title: e.target.value })}
                    required
                    className="bg-white/50"
                    data-testid="resource-title-input"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={resourceForm.description}
                    onChange={(e) => setResourceForm({ ...resourceForm, description: e.target.value })}
                    required
                    className="bg-white/50"
                    data-testid="resource-description-input"
                  />
                </div>
                <div>
                  <Label>File URL</Label>
                  <Input
                    value={resourceForm.file_url}
                    onChange={(e) => setResourceForm({ ...resourceForm, file_url: e.target.value })}
                    required
                    placeholder="https://..."
                    className="bg-white/50"
                    data-testid="resource-url-input"
                  />
                </div>
                <div>
                  <Label>Category</Label>
                  <Input
                    value={resourceForm.category}
                    onChange={(e) => setResourceForm({ ...resourceForm, category: e.target.value })}
                    required
                    placeholder="e.g., Anxiety, Depression"
                    className="bg-white/50"
                    data-testid="resource-category-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="bg-primary text-white" data-testid="submit-resource-button">Create Resource</Button>
                  <Button type="button" variant="outline" onClick={() => setShowResourceForm(false)} data-testid="cancel-resource-button">Cancel</Button>
                </div>
              </form>
            </Card>
          )}

          {loading ? (
            <p className="text-center text-muted-foreground py-12">Loading resources...</p>
          ) : resources.length === 0 ? (
            <Card className="bg-white/80 backdrop-blur-sm rounded-2xl p-12 text-center">
              <p className="text-muted-foreground">No resources created yet</p>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {resources.map((resource) => (
                <Card key={resource.id} className="bg-white/80 backdrop-blur-sm rounded-2xl p-6" data-testid={`admin-resource-${resource.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <FileText className="text-primary" size={24} />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteResource(resource.id)}
                      className="text-destructive hover:bg-destructive/10"
                      data-testid={`delete-resource-${resource.id}`}
                    >
                      <X size={16} />
                    </Button>
                  </div>
                  <h3 className="text-lg font-heading font-medium text-foreground mb-2">{resource.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3">{resource.description}</p>
                  <span className="text-xs px-3 py-1 bg-muted rounded-full">{resource.category}</span>
                </Card>
              ))}
            </div>
          )}
        </section>
        )}

        {/* Slot Management */}
        {activeTab === 'slots' && (
        <section className="mt-12" data-testid="slots-management">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
            <h2 className="text-3xl font-heading font-medium text-foreground">Manage Slots</h2>
            <div className="flex flex-wrap gap-3 items-center">
              {/* Timezone Selector */}
              <div className="flex items-center gap-2">
                <Globe size={16} className="text-muted-foreground" />
                <Select value={adminTimezone} onValueChange={setAdminTimezone}>
                  <SelectTrigger className="w-48 bg-white/50" data-testid="admin-timezone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONE_OPTIONS.map((tz) => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={clearAllSlots}
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50 rounded-full"
                data-testid="clear-all-slots-button"
              >
                <Trash2 size={16} className="mr-2" /> Clear All Slots
              </Button>
              <Button
                onClick={() => setShowSlotForm(!showSlotForm)}
                className="bg-primary text-white hover:bg-primary/90 rounded-full"
                data-testid="add-slot-button"
              >
                <Upload size={16} className="mr-2" /> Add Slots
              </Button>
            </div>
          </div>

          {showSlotForm && (
            <Card className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 mb-6" data-testid="slot-form">
              <h3 className="text-xl font-heading font-medium text-foreground mb-6">Set Up Recurring Availability</h3>
              
              <form onSubmit={createSlots} className="space-y-6">
                {/* Date Range */}
                <div>
                  <Label className="text-base font-medium mb-3 block">Date Range</Label>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-muted-foreground">Start Date</Label>
                      <Input
                        type="date"
                        value={slotForm.start_date}
                        onChange={(e) => setSlotForm({ ...slotForm, start_date: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                        required
                        className="bg-white/50 mt-1"
                        data-testid="slot-start-date"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground">End Date</Label>
                      <Input
                        type="date"
                        value={slotForm.end_date}
                        onChange={(e) => setSlotForm({ ...slotForm, end_date: e.target.value })}
                        min={slotForm.start_date || new Date().toISOString().split('T')[0]}
                        required
                        className="bg-white/50 mt-1"
                        data-testid="slot-end-date"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Slots will be created for all selected days within this period
                  </p>
                </div>

                {/* Quick Schedules */}
                <div>
                  <Label className="text-base font-medium mb-3 block">Quick Schedule Templates</Label>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setQuickSchedule('weekdays')}
                      className="text-sm"
                    >
                      Tue-Fri Schedule
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setQuickSchedule('saturday')}
                      className="text-sm"
                    >
                      Saturday Schedule
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setQuickSchedule('all')}
                      className="text-sm"
                    >
                      Full Week (Tue-Sat)
                    </Button>
                  </div>
                </div>

                {/* Days of Week */}
                <div>
                  <Label className="text-base font-medium mb-3 block">Select Days</Label>
                  <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
                    {weekdayOptions.map((day) => (
                      <button
                        key={day.value}
                        type="button"
                        onClick={() => toggleWeekday(day.value)}
                        className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                          slotForm.weekdays.includes(day.value)
                            ? 'border-primary bg-primary text-white'
                            : 'border-muted hover:border-primary/50'
                        }`}
                        data-testid={`weekday-${day.value}`}
                      >
                        {day.label.slice(0, 3)}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {slotForm.weekdays.length > 0 
                      ? `${slotForm.weekdays.length} day(s) selected` 
                      : 'Select days when you want to be available'}
                  </p>
                </div>

                {/* Time Slots */}
                <div>
                  <Label className="text-base font-medium mb-1 block">Time Slots</Label>
                  <p className="text-sm text-muted-foreground mb-3">
                    Enter times separated by commas (e.g., 11:00, 12:30, 16:00, 17:30)
                  </p>
                  <Input
                    value={slotForm.times}
                    onChange={(e) => setSlotForm({ ...slotForm, times: e.target.value })}
                    required
                    placeholder="11:00, 12:30, 16:00, 17:30"
                    className="bg-white/50"
                    data-testid="slot-times-input"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Minimum 30-minute gap required between slots
                  </p>
                </div>

                {/* Slot Type */}
                <div>
                  <Label className="text-base font-medium mb-3 block">Session Type</Label>
                  <div className="grid md:grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setSlotForm({ ...slotForm, slot_type: 'individual' })}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        slotForm.slot_type === 'individual'
                          ? 'border-primary bg-primary/5'
                          : 'border-muted hover:border-primary/50'
                      }`}
                    >
                      <div className="font-medium text-foreground">Individual Sessions</div>
                      <div className="text-sm text-muted-foreground">60 minutes</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setSlotForm({ ...slotForm, slot_type: 'couples' })}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        slotForm.slot_type === 'couples'
                          ? 'border-primary bg-primary/5'
                          : 'border-muted hover:border-primary/50'
                      }`}
                    >
                      <div className="font-medium text-foreground">Couples Sessions</div>
                      <div className="text-sm text-muted-foreground">75 minutes</div>
                    </button>
                  </div>
                </div>

                {/* Summary */}
                {slotForm.start_date && slotForm.end_date && slotForm.weekdays.length > 0 && slotForm.times && (
                  <div className="bg-secondary/30 rounded-xl p-4 border border-primary/20">
                    <h4 className="font-medium text-foreground mb-2">Preview</h4>
                    <p className="text-sm text-muted-foreground">
                      Creating <strong>{slotForm.slot_type}</strong> slots from{' '}
                      <strong>{slotForm.start_date}</strong> to <strong>{slotForm.end_date}</strong>
                      {' '}on <strong>{slotForm.weekdays.length}</strong> day(s) per week
                      {' '}with <strong>{slotForm.times.split(',').filter(t => t.trim()).length}</strong> time slot(s) each day.
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <Button 
                    type="submit" 
                    className="flex-1 bg-primary text-white hover:bg-primary/90" 
                    data-testid="submit-slot-button"
                  >
                    Create Slots
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      setShowSlotForm(false);
                      setSlotForm({ start_date: '', end_date: '', weekdays: [], times: '', slot_type: 'individual' });
                    }}
                    className="flex-1"
                    data-testid="cancel-slot-button"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Card>
          )}

          {loading ? (
            <p className="text-center text-muted-foreground py-12">Loading slots...</p>
          ) : slots.length === 0 ? (
            <Card className="bg-white/80 backdrop-blur-sm rounded-2xl p-12 text-center">
              <p className="text-muted-foreground">No slots created yet</p>
            </Card>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-muted">
                    <th className="text-left p-4 font-heading font-medium">Date</th>
                    <th className="text-left p-4 font-heading font-medium">
                      Time ({getTimezoneAbbreviation(adminTimezone)})
                    </th>
                    <th className="text-left p-4 font-heading font-medium">Type</th>
                    <th className="text-left p-4 font-heading font-medium">Status</th>
                    <th className="text-left p-4 font-heading font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {slots.map((slot) => {
                    const converted = convertFromIST(slot.date, slot.time, adminTimezone);
                    return (
                      <tr key={slot.id} className="border-b border-muted/50 hover:bg-muted/20" data-testid={`slot-row-${slot.id}`}>
                        <td className="p-4">
                          {converted.dateChanged ? converted.date : slot.date}
                          {converted.dateChanged && (
                            <span className="text-xs text-muted-foreground ml-1">(IST: {slot.date})</span>
                          )}
                        </td>
                        <td className="p-4">{formatTimeDisplay(converted.time)}</td>
                        <td className="p-4">
                          <span className="px-3 py-1 bg-muted rounded-full text-xs">
                            {slot.slot_type === 'individual' ? 'Individual (60 min)' : 'Couples (75 min)'}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            slot.is_booked ? 'bg-accent/10 text-accent' : 'bg-primary/10 text-primary'
                          }`}>
                            {slot.is_booked ? 'Booked' : 'Available'}
                          </span>
                        </td>
                        <td className="p-4">
                          {!slot.is_booked && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteSlot(slot.id)}
                              className="text-destructive hover:bg-destructive/10"
                              data-testid={`delete-slot-${slot.id}`}
                            >
                              <X size={16} />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
        )}

        {/* Pricing Management */}
        {activeTab === 'pricing' && (
        <section className="mt-12" data-testid="pricing-management">
          <div className="mb-6">
            <h2 className="text-3xl font-heading font-medium text-foreground">Manage Pricing</h2>
            <p className="text-muted-foreground mt-2">Update therapy session rates for India (INR) and International (USD) clients</p>
          </div>

          {loading ? (
            <p className="text-center text-muted-foreground py-12">Loading prices...</p>
          ) : (
            <div className="grid md:grid-cols-3 gap-6">
              {prices.map((price) => (
                <Card key={price.service_name} className="bg-white/80 backdrop-blur-sm rounded-2xl p-6" data-testid={`price-card-${price.service_name.toLowerCase().replace(' ', '-')}`}>
                  <h3 className="text-xl font-heading font-medium text-foreground mb-4">{price.service_name}</h3>
                  
                  {editingPrice === price.service_name ? (
                    <div className="space-y-4">
                      <div>
                        <Label className="text-sm">India Price (INR)</Label>
                        <Input
                          type="number"
                          defaultValue={price.price_inr}
                          id={`inr-${price.service_name}`}
                          className="bg-white/50 mt-1"
                          data-testid={`price-inr-input-${price.service_name.toLowerCase().replace(' ', '-')}`}
                        />
                      </div>
                      <div>
                        <Label className="text-sm">International Price (USD)</Label>
                        <Input
                          type="number"
                          defaultValue={price.price_usd}
                          id={`usd-${price.service_name}`}
                          className="bg-white/50 mt-1"
                          data-testid={`price-usd-input-${price.service_name.toLowerCase().replace(' ', '-')}`}
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Duration (minutes)</Label>
                        <Input
                          type="number"
                          defaultValue={price.duration}
                          id={`duration-${price.service_name}`}
                          className="bg-white/50 mt-1"
                          data-testid={`price-duration-input-${price.service_name.toLowerCase().replace(' ', '-')}`}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => {
                            const priceInr = parseFloat(document.getElementById(`inr-${price.service_name}`).value);
                            const priceUsd = parseFloat(document.getElementById(`usd-${price.service_name}`).value);
                            const duration = parseInt(document.getElementById(`duration-${price.service_name}`).value);
                            updatePrice(price.service_name, {
                              price_inr: priceInr,
                              price_usd: priceUsd,
                              duration: duration
                            });
                          }}
                          className="flex-1 bg-primary text-white"
                          data-testid={`save-price-${price.service_name.toLowerCase().replace(' ', '-')}`}
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingPrice(null)}
                          className="flex-1"
                          data-testid={`cancel-price-${price.service_name.toLowerCase().replace(' ', '-')}`}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center py-2 border-b border-muted">
                        <span className="text-sm text-muted-foreground">India</span>
                        <span className="text-lg font-bold text-accent">₹{price.price_inr}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-muted">
                        <span className="text-sm text-muted-foreground">International</span>
                        <span className="text-lg font-bold text-accent">${price.price_usd}</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm text-muted-foreground">Duration</span>
                        <span className="text-sm font-medium">{price.duration} mins</span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingPrice(price.service_name)}
                        className="w-full"
                        data-testid={`edit-price-${price.service_name.toLowerCase().replace(' ', '-')}`}
                      >
                        Edit Pricing
                      </Button>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </section>
        )}

        {/* Google Calendar Integration */}
        {activeTab === 'calendar' && (
        <section className="mt-12" data-testid="calendar-management">
          <div className="mb-6">
            <h2 className="text-3xl font-heading font-medium text-foreground">Google Calendar Integration</h2>
            <p className="text-muted-foreground mt-2">Connect your Google Calendar to automatically sync appointments and check availability</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Connection Status Card */}
            <Card className="bg-white/80 backdrop-blur-sm rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className={`p-3 rounded-full ${calendarStatus.connected ? 'bg-green-100' : 'bg-muted'}`}>
                  <CalendarCheck className={calendarStatus.connected ? 'text-green-600' : 'text-muted-foreground'} size={28} />
                </div>
                <div>
                  <h3 className="text-xl font-heading font-medium text-foreground">
                    {calendarStatus.connected ? 'Calendar Connected' : 'Connect Your Calendar'}
                  </h3>
                  {calendarStatus.connected ? (
                    <p className="text-sm text-muted-foreground mt-1">
                      Connected as: <strong>{calendarStatus.email}</strong>
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground mt-1">
                      Link your Google Calendar to enable automatic availability checking
                    </p>
                  )}
                </div>
              </div>

              {calendarStatus.connected ? (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-green-700 mb-2">
                      <Link2 size={16} />
                      <span className="font-medium">Active Features</span>
                    </div>
                    <ul className="text-sm text-green-600 space-y-1 ml-6">
                      <li>✓ Real-time availability checking</li>
                      <li>✓ Automatic event creation on booking</li>
                      <li>✓ Calendar invite sent to clients</li>
                      <li>✓ Busy time slots automatically hidden</li>
                      <li>✓ Auto-sync every 15 minutes</li>
                    </ul>
                  </div>

                  {/* Sync Status & Manual Refresh */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 text-blue-700">
                        <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} />
                        <span className="font-medium">Calendar Sync</span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => syncCalendar(false)}
                        disabled={syncing}
                        className="border-blue-300 text-blue-700 hover:bg-blue-100"
                        data-testid="manual-sync-btn"
                      >
                        {syncing ? 'Syncing...' : 'Sync Now'}
                      </Button>
                    </div>
                    <p className="text-xs text-blue-600">
                      {lastCalendarSync 
                        ? `Last synced: ${lastCalendarSync.toLocaleTimeString()}`
                        : 'Auto-syncs every 15 minutes'
                      }
                    </p>
                    <p className="text-xs text-blue-500 mt-1">
                      Changes made on Google Calendar will reflect on available slots
                    </p>
                  </div>
                  
                  <Button
                    variant="outline"
                    onClick={disconnectGoogleCalendar}
                    className="w-full border-red-200 text-red-600 hover:bg-red-50"
                    data-testid="disconnect-calendar-btn"
                  >
                    <Unlink size={16} className="mr-2" />
                    Disconnect Calendar
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-secondary/50 rounded-xl p-4">
                    <p className="text-sm text-muted-foreground">
                      When connected, your calendar will be used to:
                    </p>
                    <ul className="text-sm text-muted-foreground mt-2 space-y-1 ml-4">
                      <li>• Hide slots that conflict with your existing events</li>
                      <li>• Create calendar events when clients book sessions</li>
                      <li>• Send calendar invites to clients automatically</li>
                    </ul>
                  </div>
                  
                  <Button
                    onClick={connectGoogleCalendar}
                    disabled={connectingCalendar}
                    className="w-full bg-primary text-white hover:bg-primary/90"
                    data-testid="connect-calendar-btn"
                  >
                    {connectingCalendar ? (
                      'Connecting...'
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                          <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Connect Google Calendar
                      </>
                    )}
                  </Button>
                </div>
              )}
            </Card>

            {/* How It Works Card */}
            <Card className="bg-white/80 backdrop-blur-sm rounded-2xl p-8">
              <h3 className="text-xl font-heading font-medium text-foreground mb-6">How It Works</h3>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold">
                    1
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground">Connect Once</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Click the connect button and authorize access to your Google Calendar
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold">
                    2
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground">Automatic Availability</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      When clients view available slots, any times that conflict with your calendar events are automatically hidden
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold">
                    3
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground">Seamless Booking</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      When a client books a session, an event is automatically created on your calendar with all the session details
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold">
                    4
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground">Client Invites</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Clients automatically receive a calendar invite with session details and reminders
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </section>
        )}

        {/* Vacation/Time Off Management */}
        {activeTab === 'vacations' && (
        <section className="mt-12" data-testid="vacations-management">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-3xl font-heading font-medium text-foreground">Time Off Management</h2>
              <p className="text-muted-foreground mt-2">Block out dates when you're unavailable for sessions or discovery calls</p>
            </div>
            <Button
              onClick={() => setShowVacationForm(!showVacationForm)}
              className="bg-primary text-white hover:bg-primary/90 rounded-full"
              data-testid="add-vacation-button"
            >
              <Palmtree size={16} className="mr-2" /> Add Time Off
            </Button>
          </div>

          {showVacationForm && (
            <Card className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 mb-6" data-testid="vacation-form">
              <h3 className="text-xl font-heading font-medium text-foreground mb-6">Schedule Time Off</h3>
              
              <form onSubmit={async (e) => {
                e.preventDefault();
                if (!vacationForm.start_date || !vacationForm.end_date) {
                  toast.error('Please select start and end dates');
                  return;
                }
                if (new Date(vacationForm.end_date) < new Date(vacationForm.start_date)) {
                  toast.error('End date must be after start date');
                  return;
                }
                try {
                  const token = localStorage.getItem('admin_token');
                  await authAxios.post(`${API}/admin/vacations`, vacationForm, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  toast.success('Time off scheduled successfully');
                  setShowVacationForm(false);
                  setVacationForm({ start_date: '', end_date: '', reason: '' });
                  fetchData();
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to schedule time off');
                }
              }} className="space-y-6">
                {/* Date Range */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-muted-foreground">Start Date</Label>
                    <Input
                      type="date"
                      value={vacationForm.start_date}
                      onChange={(e) => setVacationForm({ ...vacationForm, start_date: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                      required
                      className="bg-white/50 mt-1"
                      data-testid="vacation-start-date"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">End Date</Label>
                    <Input
                      type="date"
                      value={vacationForm.end_date}
                      onChange={(e) => setVacationForm({ ...vacationForm, end_date: e.target.value })}
                      min={vacationForm.start_date || new Date().toISOString().split('T')[0]}
                      required
                      className="bg-white/50 mt-1"
                      data-testid="vacation-end-date"
                    />
                  </div>
                </div>

                {/* Reason */}
                <div>
                  <Label className="text-sm text-muted-foreground">Reason (optional)</Label>
                  <Input
                    value={vacationForm.reason}
                    onChange={(e) => setVacationForm({ ...vacationForm, reason: e.target.value })}
                    placeholder="e.g., Holiday, Conference, Personal leave"
                    className="bg-white/50 mt-1"
                    data-testid="vacation-reason"
                  />
                </div>

                {/* Info Box */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-sm text-amber-700">
                    <strong>Note:</strong> During this period, all session slots and discovery call slots will be automatically hidden from clients.
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <Button 
                    type="submit" 
                    className="flex-1 bg-primary text-white hover:bg-primary/90" 
                    data-testid="submit-vacation-button"
                  >
                    Schedule Time Off
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      setShowVacationForm(false);
                      setVacationForm({ start_date: '', end_date: '', reason: '' });
                    }}
                    className="flex-1"
                    data-testid="cancel-vacation-button"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Card>
          )}

          {loading ? (
            <p className="text-center text-muted-foreground py-12">Loading time off periods...</p>
          ) : vacations.length === 0 ? (
            <Card className="bg-white/80 backdrop-blur-sm rounded-2xl p-12 text-center">
              <Palmtree className="mx-auto text-muted-foreground mb-4" size={48} />
              <h3 className="text-lg font-heading font-medium text-foreground mb-2">No Time Off Scheduled</h3>
              <p className="text-muted-foreground mb-6">You haven't scheduled any vacation or time off periods yet.</p>
              <Button
                onClick={() => setShowVacationForm(true)}
                variant="outline"
                data-testid="empty-add-vacation-button"
              >
                <Palmtree size={16} className="mr-2" /> Add Time Off
              </Button>
            </Card>
          ) : (
            <div className="space-y-4">
              {vacations.map((vacation) => {
                const startDate = new Date(vacation.start_date);
                const endDate = new Date(vacation.end_date);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const isActive = today >= startDate && today <= endDate;
                const isUpcoming = today < startDate;
                const isPast = today > endDate;
                
                return (
                  <Card 
                    key={vacation.id} 
                    className={`bg-white/80 backdrop-blur-sm rounded-2xl p-6 border-l-4 ${
                      isActive ? 'border-l-amber-500' : 
                      isUpcoming ? 'border-l-primary' : 
                      'border-l-muted'
                    }`}
                    data-testid={`vacation-item-${vacation.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-full ${
                          isActive ? 'bg-amber-100' : 
                          isUpcoming ? 'bg-primary/10' : 
                          'bg-muted'
                        }`}>
                          <Palmtree className={`${
                            isActive ? 'text-amber-600' : 
                            isUpcoming ? 'text-primary' : 
                            'text-muted-foreground'
                          }`} size={24} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-heading font-medium text-foreground">
                              {vacation.start_date === vacation.end_date 
                                ? new Date(vacation.start_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                                : `${new Date(vacation.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(vacation.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                              }
                            </h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              isActive ? 'bg-amber-100 text-amber-700' : 
                              isUpcoming ? 'bg-primary/10 text-primary' : 
                              'bg-muted text-muted-foreground'
                            }`}>
                              {isActive ? 'Active' : isUpcoming ? 'Upcoming' : 'Past'}
                            </span>
                          </div>
                          {vacation.reason && (
                            <p className="text-sm text-muted-foreground">{vacation.reason}</p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            {(() => {
                              const diffTime = Math.abs(endDate - startDate);
                              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                              return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
                            })()}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('admin_token');
                            await authAxios.delete(`${API}/admin/vacations/${vacation.id}`, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            toast.success('Time off removed');
                            fetchData();
                          } catch (error) {
                            toast.error('Failed to remove time off');
                          }
                        }}
                        className="text-destructive hover:bg-destructive/10"
                        data-testid={`delete-vacation-${vacation.id}`}
                      >
                        <Trash2 size={16} className="mr-1" /> Remove
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </section>
        )}

        {/* Website Manager Agent Tab */}
        {activeTab === 'agent' && (
        <section className="mt-12" data-testid="agent-section">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-3xl font-heading font-medium text-foreground flex items-center gap-2">
                <Bot size={28} /> Website Manager Agent
              </h2>
              <p className="text-muted-foreground mt-1">Monitor your website health and chat with your AI assistant</p>
            </div>
            <Button
              onClick={fetchSystemHealth}
              variant="outline"
              className="rounded-full"
              data-testid="refresh-health-button"
            >
              <RefreshCw size={16} className="mr-2" /> Refresh Status
            </Button>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* System Health Overview */}
            <div className="lg:col-span-1 space-y-4">
              <Card className="bg-white/80 backdrop-blur-sm rounded-2xl p-6">
                <h3 className="text-lg font-heading font-medium text-foreground mb-4 flex items-center gap-2">
                  <Activity size={20} /> System Health
                </h3>
                
                {systemHealth ? (
                  <div className="space-y-3">
                    {/* Overall Status */}
                    <div className={`p-3 rounded-xl flex items-center gap-3 ${
                      systemHealth.overall_status === 'healthy' ? 'bg-green-50 text-green-700' :
                      systemHealth.overall_status === 'warning' ? 'bg-amber-50 text-amber-700' :
                      'bg-red-50 text-red-700'
                    }`}>
                      {systemHealth.overall_status === 'healthy' ? (
                        <CheckCircle size={24} />
                      ) : systemHealth.overall_status === 'warning' ? (
                        <AlertTriangle size={24} />
                      ) : (
                        <AlertCircle size={24} />
                      )}
                      <div>
                        <p className="font-medium">
                          {systemHealth.overall_status === 'healthy' ? 'All Systems Operational' :
                           systemHealth.overall_status === 'warning' ? 'Some Warnings' :
                           'Issues Detected'}
                        </p>
                        <p className="text-xs opacity-75">
                          Last checked: {new Date(systemHealth.checked_at).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>

                    {/* Individual Services */}
                    <div className="space-y-2">
                      {systemHealth.services?.map((service) => (
                        <div 
                          key={service.name}
                          className="flex items-center justify-between p-2 rounded-lg bg-secondary/30"
                        >
                          <span className="text-sm text-foreground">{service.name}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            service.status === 'healthy' ? 'bg-green-100 text-green-700' :
                            service.status === 'warning' ? 'bg-amber-100 text-amber-700' :
                            service.status === 'error' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {service.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Activity size={32} className="mx-auto text-muted-foreground mb-2 animate-pulse" />
                    <p className="text-sm text-muted-foreground">Loading system health...</p>
                  </div>
                )}
              </Card>

              {/* Active Alerts */}
              <Card className="bg-white/80 backdrop-blur-sm rounded-2xl p-6">
                <h3 className="text-lg font-heading font-medium text-foreground mb-4 flex items-center gap-2">
                  <AlertTriangle size={20} /> Active Alerts
                  {alerts.length > 0 && (
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">{alerts.length}</span>
                  )}
                </h3>
                
                {alerts.length === 0 ? (
                  <div className="text-center py-6">
                    <CheckCircle size={32} className="mx-auto text-green-500 mb-2" />
                    <p className="text-sm text-muted-foreground">No active alerts</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {alerts.map((alert) => (
                      <div 
                        key={alert.id || alert.title}
                        className={`p-3 rounded-xl border-l-4 ${
                          alert.alert_type === 'critical' ? 'bg-red-50 border-red-500' :
                          alert.alert_type === 'warning' ? 'bg-amber-50 border-amber-500' :
                          'bg-blue-50 border-blue-500'
                        }`}
                      >
                        <p className="font-medium text-sm">{alert.title}</p>
                        <p className="text-xs text-muted-foreground mt-1">{alert.message}</p>
                        {alert.action_required && (
                          <p className="text-xs text-primary mt-2 font-medium">
                            Action: {alert.action_required}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>

            {/* Chat Interface */}
            <div className="lg:col-span-2">
              <Card className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 h-full flex flex-col">
                <h3 className="text-lg font-heading font-medium text-foreground mb-4 flex items-center gap-2">
                  <Bot size={20} /> Chat with Assistant
                </h3>
                
                {/* Chat Messages */}
                <div 
                  ref={agentChatRef}
                  className="flex-1 min-h-[400px] max-h-[500px] overflow-y-auto space-y-4 mb-4 p-4 bg-secondary/20 rounded-xl"
                >
                  {agentMessages.length === 0 ? (
                    <div className="text-center py-12">
                      <Bot size={48} className="mx-auto text-primary/30 mb-4" />
                      <p className="text-muted-foreground mb-2">Hello! I'm your Website Manager Agent.</p>
                      <p className="text-sm text-muted-foreground">Ask me about your system status, calendar health, or any concerns.</p>
                      <div className="mt-4 flex flex-wrap justify-center gap-2">
                        <button 
                          onClick={() => { setAgentInput('Check system status'); }}
                          className="px-3 py-1.5 text-xs bg-primary/10 text-primary rounded-full hover:bg-primary/20"
                        >
                          Check status
                        </button>
                        <button 
                          onClick={() => { setAgentInput('Any alerts?'); }}
                          className="px-3 py-1.5 text-xs bg-primary/10 text-primary rounded-full hover:bg-primary/20"
                        >
                          View alerts
                        </button>
                        <button 
                          onClick={() => { setAgentInput('Calendar health'); }}
                          className="px-3 py-1.5 text-xs bg-primary/10 text-primary rounded-full hover:bg-primary/20"
                        >
                          Calendar status
                        </button>
                        <button 
                          onClick={() => { setAgentInput('Help'); }}
                          className="px-3 py-1.5 text-xs bg-primary/10 text-primary rounded-full hover:bg-primary/20"
                        >
                          What can you do?
                        </button>
                      </div>
                    </div>
                  ) : (
                    agentMessages.map((msg, idx) => (
                      <div 
                        key={msg.timestamp || idx}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div 
                          className={`max-w-[80%] p-4 rounded-2xl ${
                            msg.role === 'user' 
                              ? 'bg-primary text-white rounded-br-sm' 
                              : 'bg-white border border-muted rounded-bl-sm'
                          }`}
                        >
                          <div className="whitespace-pre-wrap text-sm">
                            {msg.content.split('\n').map((line, i) => (
                              <p key={i} className={line.startsWith('**') ? 'font-semibold' : ''}>
                                {line.replace(/\*\*/g, '')}
                              </p>
                            ))}
                          </div>
                          <p className={`text-xs mt-2 ${msg.role === 'user' ? 'text-white/70' : 'text-muted-foreground'}`}>
                            {new Date(msg.timestamp).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                  
                  {agentLoading && (
                    <div className="flex justify-start">
                      <div className="bg-white border border-muted p-4 rounded-2xl rounded-bl-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Chat Input */}
                <div className="flex gap-2">
                  <Input
                    value={agentInput}
                    onChange={(e) => setAgentInput(e.target.value)}
                    onKeyPress={handleAgentKeyPress}
                    placeholder="Ask about system health, calendar, payments..."
                    className="flex-1 bg-white/50 rounded-full px-4"
                    disabled={agentLoading}
                    data-testid="agent-chat-input"
                  />
                  <Button
                    onClick={sendAgentMessage}
                    disabled={agentLoading || !agentInput.trim()}
                    className="bg-primary text-white hover:bg-primary/90 rounded-full px-6"
                    data-testid="agent-send-button"
                  >
                    <Send size={18} />
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </section>
        )}

        {/* Workshop Management */}
        {activeTab === 'workshop' && (
        <section className="mb-12" data-testid="workshop-management">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-heading font-semibold text-foreground">Book Journaling Workshop</h2>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={fetchWorkshopData} disabled={workshopLoading} data-testid="workshop-refresh">
                <RefreshCw size={14} className={`mr-1.5 ${workshopLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={downloadWorkshopCSV} data-testid="workshop-download-csv">
                <Download size={14} className="mr-1.5" />
                Export CSV
              </Button>
            </div>
          </div>

          {/* Workshop Summary Cards */}
          {workshopData && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card className="p-4 rounded-xl bg-card border border-border">
                <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-1">Date</p>
                <p className="text-base font-semibold text-foreground">{workshopData.date || 'TBA'}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{workshopData.time || ''}</p>
              </Card>
              <Card className="p-4 rounded-xl bg-card border border-border">
                <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-1">Seats</p>
                <p className="text-2xl font-bold text-primary">{workshopData.seats_taken || 0}<span className="text-base font-normal text-muted-foreground"> / {workshopData.max_seats || 8}</span></p>
                <p className="text-xs text-muted-foreground mt-0.5">{workshopData.seats_remaining || 0} remaining</p>
              </Card>
              <Card className="p-4 rounded-xl bg-card border border-border">
                <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-1">Price</p>
                <p className="text-base font-semibold text-foreground">{'\u20B9'}{workshopData.price_per_seat || 999}/seat</p>
                <p className="text-xs text-muted-foreground mt-0.5">1+1: {'\u20B9'}{workshopData.pair_price_per_seat || 899} each</p>
              </Card>
              <Card className="p-4 rounded-xl bg-card border border-border">
                <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-1">Calendar</p>
                <p className="text-base font-semibold text-foreground">
                  {workshopData.calendar_event_id
                    ? <span className="text-green-600 flex items-center gap-1"><CheckCircle size={14} /> Linked</span>
                    : <span className="text-amber-600 flex items-center gap-1"><AlertCircle size={14} /> Not linked</span>
                  }
                </p>
              </Card>
            </div>
          )}

          {/* Registrations Table */}
          <Card className="rounded-xl border border-border overflow-hidden">
            <div className="p-4 border-b border-border bg-secondary/30">
              <h3 className="font-semibold text-foreground">Registrations ({workshopRegistrations.filter(r => r.status !== 'cancelled').length} active)</h3>
            </div>
            {workshopLoading ? (
              <div className="p-8 text-center text-muted-foreground">Loading...</div>
            ) : workshopRegistrations.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">No registrations yet</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-secondary/20">
                      <th className="text-left p-3 font-semibold text-foreground">Name</th>
                      <th className="text-left p-3 font-semibold text-foreground">Email</th>
                      <th className="text-left p-3 font-semibold text-foreground">Phone</th>
                      <th className="text-center p-3 font-semibold text-foreground">Age</th>
                      <th className="text-center p-3 font-semibold text-foreground">Sex</th>
                      <th className="text-center p-3 font-semibold text-foreground">Seats</th>
                      <th className="text-right p-3 font-semibold text-foreground">Amount</th>
                      <th className="text-center p-3 font-semibold text-foreground">Status</th>
                      <th className="text-center p-3 font-semibold text-foreground">Date</th>
                      <th className="text-center p-3 font-semibold text-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workshopRegistrations.map(reg => (
                      <tr key={reg.id} className={`border-b border-border last:border-0 ${reg.status === 'cancelled' ? 'opacity-50' : ''}`}>
                        <td className="p-3 font-medium text-foreground">{reg.name}</td>
                        <td className="p-3 text-muted-foreground">{reg.email}</td>
                        <td className="p-3 text-muted-foreground">{reg.phone || '-'}</td>
                        <td className="p-3 text-center text-muted-foreground">{reg.age || '-'}</td>
                        <td className="p-3 text-center text-muted-foreground">{reg.sex || '-'}</td>
                        <td className="p-3 text-center text-foreground">{reg.quantity || 1}</td>
                        <td className="p-3 text-right text-foreground">{'\u20B9'}{reg.total_amount || reg.price_per_seat || '-'}</td>
                        <td className="p-3 text-center">
                          {reg.status === 'cancelled' && reg.payment_status === 'refunded' ? (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">Refunded</span>
                          ) : reg.status === 'cancelled' ? (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Cancelled</span>
                          ) : reg.payment_status === 'completed' ? (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Paid</span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">{reg.status}</span>
                          )}
                        </td>
                        <td className="p-3 text-center text-xs text-muted-foreground">
                          {reg.registered_at ? new Date(reg.registered_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="p-3 text-center">
                          {reg.status !== 'cancelled' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => cancelWorkshopRegistration(reg.id, reg.name)}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              data-testid={`cancel-reg-${reg.id}`}
                            >
                              <XCircle size={14} className="mr-1" />
                              Cancel
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </section>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
